/* eslint-disable react/require-default-props, react/forbid-prop-types, no-nested-ternary, max-len */
import React from 'react';
import PropTypes from 'prop-types';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Video,
} from 'lucide-react';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Progress } from '../../../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import reviewConstants from '../../../../constants/review.constants';
import { cn } from '../../../../lib/utils';

const columns = [
  { id: 'reviewGoal', name: 'Review Goal', sortable: true },
  { id: 'newGoal', name: 'New Goal', sortable: true },
  { id: 'status', name: 'Status', sortable: true },
  { id: 'confirmed', name: 'Confirmed', sortable: true, align: 'center' },
  { id: 'declined', name: 'Declined', sortable: true, align: 'center' },
  { id: 'progress', name: 'Progress', sortable: true, align: 'center' },
  { id: 'updatedAt', name: 'Review Date', sortable: true },
  { id: 'reviewRecordLinks', name: 'Recording', sortable: false, align: 'center' },
];

function SortIcon({ field, sortField, sortOrder }) {
  if (field !== sortField) {
    return <ChevronsUpDown className="ml-2 h-4 w-4 text-muted-foreground/50" />;
  }
  return sortOrder === 'asc' ? (
    <ChevronUp className="ml-2 h-4 w-4" />
  ) : (
    <ChevronDown className="ml-2 h-4 w-4" />
  );
}

SortIcon.propTypes = {
  field: PropTypes.string.isRequired,
  sortField: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
};

function StatusBadge({ status }) {
  const { REVIEW_STATUSES } = reviewConstants;

  const statusConfig = {
    [REVIEW_STATUSES.CONFIRMED]: {
      icon: CheckCircle2,
      label: 'Confirmed',
      className: 'bg-green-100 text-green-700 border-green-200',
    },
    [REVIEW_STATUSES.DECLINED]: {
      icon: XCircle,
      label: 'Declined',
      className: 'bg-red-100 text-red-700 border-red-200',
    },
    [REVIEW_STATUSES.PENDING]: {
      icon: Clock,
      label: 'Pending',
      className: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    },
    [REVIEW_STATUSES.PROCESSED]: {
      icon: CheckCircle2,
      label: 'Processed',
      className: 'bg-blue-100 text-blue-700 border-blue-200',
    },
    [REVIEW_STATUSES.REVERTED]: {
      icon: RotateCcw,
      label: 'Reverted',
      className: 'bg-gray-100 text-gray-700 border-gray-200',
    },
  };

  const config = statusConfig[status] || statusConfig[REVIEW_STATUSES.PENDING];
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={cn('gap-1', config.className)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

function ReviewHistoryTable({
  data,
  isLoading,
  totalCount,
  currentPage,
  rowsPerPage,
  sortField,
  sortOrder,
  onSort,
  onPageChange,
  onRowsPerPageChange,
}) {
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = currentPage * rowsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * rowsPerPage, totalCount);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={cn(
                    column.align === 'center' && 'text-center',
                    column.sortable && 'cursor-pointer select-none hover:bg-muted',
                  )}
                  onClick={() => column.sortable && onSort(column.id)}
                >
                  <div
                    className={cn(
                      'flex items-center',
                      column.align === 'center' && 'justify-center',
                    )}
                  >
                    {column.name}
                    {column.sortable && (
                      <SortIcon field={column.id} sortField={sortField} sortOrder={sortOrder} />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: rowsPerPage }, (_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {columns.map((column) => (
                      <TableCell key={column.id}>
                        <div className="h-4 bg-muted animate-pulse rounded" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : data.map((review) => (
                  <TableRow key={review._id}>
                    <TableCell className="font-medium">{review.reviewGoal}</TableCell>
                    <TableCell>{review.newGoal}</TableCell>
                    <TableCell>
                      <StatusBadge status={review.status} />
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium rounded-full bg-green-100 text-green-700">
                        {review.confirmed}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 text-sm font-medium rounded-full bg-red-100 text-red-700">
                        {review.declined}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <Progress value={review.progress} className="h-2 flex-1" />
                        <span className="text-sm text-muted-foreground w-10 text-right">
                          {review.progress}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(review.updatedAt)}
                    </TableCell>
                    <TableCell className="text-center">
                      {(() => {
                        const links = Array.isArray(review.reviewRecordLinks)
                          ? review.reviewRecordLinks
                          : review.reviewRecordLinks
                          ? [review.reviewRecordLinks]
                          : [];

                        if (links.length === 0) {
                          return <span className="text-muted-foreground">—</span>;
                        }

                        if (links.length === 1) {
                          return (
                            <a
                              href={links[0]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                            >
                              <Video className="h-4 w-4" />
                            </a>
                          );
                        }

                        return (
                          <div className="flex items-center justify-center gap-1">
                            {links.map((link, index) => (
                              <a
                                key={link}
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                                title={`Recording ${index + 1}`}
                              >
                                <Video className="h-4 w-4" />
                              </a>
                            ))}
                          </div>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
            className="h-8 w-16 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            {startItem}-{endItem} of {totalCount}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

ReviewHistoryTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      declined: PropTypes.number.isRequired,
      confirmed: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
      reviewGoal: PropTypes.string.isRequired,
      newGoal: PropTypes.string.isRequired,
      progress: PropTypes.number.isRequired,
      reviewRecordLinks: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.string,
      ]),
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  totalCount: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  sortField: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onSort: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

export default ReviewHistoryTable;
